import React from "react";
import { createPortal } from "react-dom";
import "./Tooltip.css";

interface Props {
  /** Tooltip text/content. `\n` renders as line breaks. Null/empty = no tooltip. */
  content: React.ReactNode;
  /** Show delay in ms. Default 80 — near-instant, but enough to avoid flicker
   *  when sweeping the cursor across rows. (Native `title` is a fixed ~600ms+.) */
  delayMs?: number;
  /** A single DOM child to anchor to. Its ref + mouse handlers are augmented. */
  children: React.ReactElement;
}

/** Lightweight hover tooltip — a portal-rendered floating label that shows after
 *  a short, configurable delay (unlike native `title`, whose delay the browser
 *  controls). Positions above the anchor, flipping below near the viewport top. */
const Tooltip: React.FC<Props> = ({ content, delayMs = 80, children }) => {
  const [box, setBox] = React.useState<{ x: number; y: number; below: boolean } | null>(null);
  // Horizontal nudge applied after measuring, to keep the (center-anchored)
  // tooltip from spilling off the viewport when its anchor sits near an edge.
  const [shiftX, setShiftX] = React.useState(0);
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const tipRef = React.useRef<HTMLDivElement | null>(null);
  const timer = React.useRef<number | null>(null);

  const clear = () => {
    if (timer.current != null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const show = () => {
    clear();
    timer.current = window.setTimeout(() => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = r.top < 110; // flip under the anchor with room for a multi-line tip
      setShiftX(0); // re-center; the layout effect re-clamps for this anchor
      setBox({ x: r.left + r.width / 2, y: below ? r.bottom : r.top, below });
    }, delayMs);
  };
  const hide = () => {
    clear();
    setBox(null);
  };
  React.useEffect(() => clear, []);

  // Clamp into the viewport before paint (so there's no visible jump).
  React.useLayoutEffect(() => {
    if (!box || !tipRef.current) return;
    const pad = 8;
    const r = tipRef.current.getBoundingClientRect();
    let dx = 0;
    if (r.left < pad) dx = pad - r.left;
    else if (r.right > window.innerWidth - pad) dx = window.innerWidth - pad - r.right;
    if (dx) setShiftX((s) => s + dx);
  }, [box]);

  const childProps = children.props as {
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    ref?: React.Ref<HTMLElement>;
  };
  const child = React.cloneElement(children as React.ReactElement<any>, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
      const r = childProps.ref;
      if (typeof r === "function") r(node);
      else if (r && typeof r === "object") (r as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      childProps.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      childProps.onMouseLeave?.(e);
    },
  });

  return (
    <>
      {child}
      {box && content != null && content !== "" &&
        createPortal(
          <div
            ref={tipRef}
            className={`app-tooltip${box.below ? " app-tooltip--below" : ""}`}
            style={{ left: box.x + shiftX, top: box.y }}
            role="tooltip"
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
